import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, Button } from 'reactstrap';
import axios from 'axios';

export function LabeledPhotos() {
  const [photos,        setPhotos]        = useState([]);
  const [currentPhoto,  setCurrentPhoto]  = useState(null);

  const [manufacturer,  setManufacturer]  = useState('');
  const [shingleType,   setShingleType]   = useState('');
  const [color,         setColor]         = useState('');

  const [rotation,      setRotation]      = useState(0);          // 0 | 90 | 180 | 270
  const [labelLog,      setLabelLog]      = useState([]);

  const [sortConfig,    setSortConfig]    = useState({ key: 'name', direction: 'asc' });

  // Dynamic‑dropdown state
  const [manufacturers,     setManufacturers]   = useState([]);
  const [products,          setProducts]        = useState([]);
  const [availableColors,   setAvailableColors] = useState([]);

  /* -------------------- INITIAL LOAD -------------------- */
  useEffect(() => {
    fetchPhotos();
    loadManufacturers();
  }, []);

  /* -------------------- DATA LOADERS -------------------- */
  const fetchPhotos = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const res   = await axios.get('http://localhost:8001/api/photos/labeled', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const normalised = res.data.map((p) => ({
        ...p,
        orientation:
          p.orientation !== undefined && p.orientation !== null
            ? Number(p.orientation) || 0
            : 0,
      }));
      setPhotos(normalised);
    } catch (error) {
      console.error('Error fetching labeled photos:', error);
    }
  };

  const loadManufacturers = async () => {
    try {
      const res = await axios.get('http://localhost:8001/api/shingles/manufacturers');
      setManufacturers(res.data.manufacturers);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
    }
  };

  const loadProducts = async (selectedManufacturer, preselectedProduct = null) => {
    if (!selectedManufacturer) return;
    try {
      const res = await axios.get('http://localhost:8001/api/shingles/products', {
        params: { manufacturer: selectedManufacturer },
      });
      let list = res.data.products;
      if (preselectedProduct && !list.includes(preselectedProduct)) list = [preselectedProduct, ...list];
      setProducts(list);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const loadColors = async (selectedProduct, preselectedColor = null, selectedManufacturer = null) => {
    if (!selectedProduct) {
      setAvailableColors([]);
      return;
    }
    try {
      const res = await axios.get('http://localhost:8001/api/shingles/colors', {
        params: {
          manufacturer: selectedManufacturer || manufacturer,
          product:      selectedProduct,
        },
      });
      let list = res.data.colors;
      if (preselectedColor && !list.includes(preselectedColor)) list = [preselectedColor, ...list];
      setAvailableColors(list);
    } catch (error) {
      console.error('Error fetching colors:', error);
    }
  };

  /* -------------------- ROTATION -------------------- */
  const rotateLeft  = () => setRotation((prev) => (prev + 270) % 360);
  const rotateRight = () => setRotation((prev) => (prev + 90)  % 360);

  /* -------------------- PHOTO SELECT -------------------- */
  const preselectFields = async (photo) => {
    const mfg  = photo.manufacturer || '';
    const prod = photo.shingle_type || '';
    const clr  = photo.color || '';

    setManufacturer(mfg);
    setShingleType(prod);
    setColor(clr);

    await loadProducts(mfg, prod);
    await loadColors(prod, clr, mfg);
  };

  const handlePhotoClick = async (photo) => {
    setCurrentPhoto(photo);
    setRotation(photo.orientation ?? 0);
    await preselectFields(photo);
    loadLabelLog(photo.id);
  };

  /* -------------------- AUDIT LOG -------------------- */
  const loadLabelLog = async (photoId) => {
    try {
      const token = localStorage.getItem('idToken');
      const res   = await axios.get(`http://localhost:8001/api/photos/${photoId}/log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabelLog(res.data);
    } catch (error) {
      console.error('Error fetching label log:', error);
    }
  };

  /* -------------------- FORM HANDLERS -------------------- */
  const handleManufacturerChange = (e) => {
    const val = e.target.value;
    setManufacturer(val);
    setShingleType(''); setColor('');
    setProducts([]);    setAvailableColors([]);
    if (val) loadProducts(val);
  };

  const handleProductChange = (e) => {
    const val = e.target.value;
    setShingleType(val);
    setColor('');
    setAvailableColors([]);
    if (val) loadColors(val);
  };

  /* -------------------- SORTING -------------------- */
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedPhotos = [...photos].sort((a, b) => {
    const { key, direction } = sortConfig;
    const order = direction === 'asc' ? 1 : -1;
    if (a[key] < b[key]) return -1 * order;
    if (a[key] > b[key]) return 1 * order;
    return 0;
  });

  /* -------------------- SAVE -------------------- */
  const handleLabelPhoto = async () => {
    if (!currentPhoto) return;
    try {
      const token = localStorage.getItem('idToken');
      await axios.post(
        `http://localhost:8001/api/photos/${currentPhoto.id}/label`,
        {
          username:      localStorage.getItem('username'),
          label:         'Label',
          manufacturer,
          shingle_type:  shingleType,
          color,
          orientation:   rotation,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      loadLabelLog(currentPhoto.id);
    } catch (error) {
      console.error('Error labeling photo:', error);
    }
  };

  /* ==================== RENDER ==================== */
  return (
    <div className="page-content">
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0">Labeled Photos for Audit</h4>
          </div>
        </Col>
      </Row>

      <Row>
        {currentPhoto && (
          <Col md={5}>
            <Card>
              <CardBody>
                {/* Image + persistent rotation buttons */}
                <div className="position-relative d-inline-block mb-3">
                  {/* Left button */}
                  <Button
                    color="secondary"
                    onClick={rotateLeft}
                    className="position-absolute top-50 start-0 translate-middle-y"
                    style={{ zIndex: 2 }}
                  >
                    <i className="mdi mdi-rotate-left"></i>
                  </Button>

                  {/* Image */}
                  <img
                    src={`${currentPhoto.url}${currentPhoto.url.includes('?') ? '&' : '?'}v=${currentPhoto.orientation}`}
                    alt={currentPhoto.name}
                    className="img-fluid d-block"
                    style={{
                      transform:  `rotate(${rotation}deg)`,
                      transition: 'transform 0.25s ease',
                      maxHeight:  '60vh',
                    }}
                  />

                  {/* Right button */}
                  <Button
                    color="secondary"
                    onClick={rotateRight}
                    className="position-absolute top-50 end-0 translate-middle-y"
                    style={{ zIndex: 2 }}
                  >
                    <i className="mdi mdi-rotate-right"></i>
                  </Button>
                </div>

                {/* Dropdowns */}
                <div className="mb-2">
                  <select value={manufacturer} onChange={handleManufacturerChange} className="form-select">
                    <option value="" disabled>Manufacturer</option>
                    {manufacturers.map((m, i) => <option key={i} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="mb-2">
                  <select value={shingleType} onChange={handleProductChange} className="form-select">
                    <option value="" disabled>Product Name</option>
                    {products.map((p, i) => <option key={i} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="mb-3">
                  <select
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="form-select"
                  >
                    <option value="" disabled>Color</option>
                    {availableColors.map((c, i) => <option key={i} value={c}>{c}</option>)}
                  </select>
                </div>

                <Button color="primary" onClick={handleLabelPhoto}>Change Label &amp; Save</Button>

                {/* Audit log */}
                <div className="mt-4">
                  <h5>Audit Log:</h5>
                  {labelLog.map((entry, idx) => (
                    <div key={idx} className="border rounded p-2 mb-2">
                      <p className="mb-1">
                        <strong>{entry.username}</strong> labeled this on{' '}
                        {new Date(entry.timestamp).toLocaleString()}.
                      </p>
                      <p className="mb-1">Label: {entry.label}</p>
                      <p className="mb-1">Manufacturer: {entry.manufacturer}</p>
                      <p className="mb-1">Shingle Type: {entry.shingle_type}</p>
                      <p className="mb-1">Color: {entry.color}</p>
                      <p className="mb-0">Orientation: {entry.orientation ?? 0}&deg;</p>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        )}

        {/* Photo table */}
        <Col md={currentPhoto ? 7 : 12}>
          <Card>
            <CardBody>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                        Photo Name&nbsp;
                        {sortConfig.key === 'name' && (
                          sortConfig.direction === 'asc'
                            ? <i className="mdi mdi-chevron-up"></i>
                            : <i className="mdi mdi-chevron-down"></i>
                        )}
                      </th>
                      <th onClick={() => handleSort('upload_timestamp')} style={{ cursor: 'pointer' }}>
                        Upload Date&nbsp;
                        {sortConfig.key === 'upload_timestamp' && (
                          sortConfig.direction === 'asc'
                            ? <i className="mdi mdi-chevron-up"></i>
                            : <i className="mdi mdi-chevron-down"></i>
                        )}
                      </th>
                      <th onClick={() => handleSort('username')} style={{ cursor: 'pointer' }}>
                        Uploaded By&nbsp;
                        {sortConfig.key === 'username' && (
                          sortConfig.direction === 'asc'
                            ? <i className="mdi mdi-chevron-up"></i>
                            : <i className="mdi mdi-chevron-down"></i>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPhotos.map((photo) => (
                      <tr
                        key={photo.id}
                        onClick={() => handlePhotoClick(photo)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{photo.name}</td>
                        <td>{new Date(photo.upload_timestamp).toLocaleString()}</td>
                        <td>{photo.username}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default LabeledPhotos;





